/**
 * R1: MMCT Application & Origination Distribution.
 *
 * Renders Michael Wallace's spec layout (2025 HMDA FLD - R1 Screen, 4-15-26):
 *   - Two stacked tables: Applications (action 1,2,3,4,5,7,8) + Originations (action 1)
 *   - Rows: per Assessment Area, two rows per AA — one control band and one PBG (MMCT) band
 *   - Columns: 5 years × 6 sub-columns each (<subject bank> | % | VBAA | % | Diff. | Sig.)
 *   - Diff. only on the PBG row, color-coded:
 *       red font if bank lends LESS in MMCT than VBAA peers (positive disparity → unfavorable)
 *       green font with leading `-` if bank lends MORE in MMCT (negative disparity → favorable, sig = "NA")
 *   - Footer disclaimer when any year fell back to a prior year's aggregate
 *
 * Backend convention (r1-screen.ts):
 *   disparity is always returned as a NON-NEGATIVE absolute value;
 *   the sign is implicit in the significance: sig === 'NA' means bank > peer.
 *   Wallace's xlsx convention is signed (peer% - bank%): negative when bank > peer.
 *   We restore the sign at display time: displayed = sig === 'NA' ? -disparity : disparity.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { redliningApi, fairLendingApi } from '../../lib/api';
import { abbreviateInstitution } from '../../lib/institution';

// MMCT band labels differ by regulator (Wallace 2026-04-27).
// Defaults reflect the FDIC convention; overridden via the `mmctRegulator` prop.
const FDIC_LOWER_LABEL = '0–49.99%';
const FDIC_UPPER_LABEL = '50.00–100%';
const FRB_LOWER_LABEL = '0–50.00%';
const FRB_UPPER_LABEL = '50.01–100%';

/** MMCT, and the two sub-types FDIC/OCC methodology adds. */
type R1Segment = 'MMCT' | 'MBT' | 'MHT';

/** Michael Wallace 2026-10-05: "MMCT tables… MBT tables… MHT tables, in that order." */
const SEGMENT_ORDER: R1Segment[] = ['MMCT', 'MBT', 'MHT'];

const SEGMENT_TITLE: Record<R1Segment, string> = {
  MMCT: 'Majority-Minority Census Tracts',
  MBT: 'Majority Black Tracts',
  MHT: 'Majority Hispanic Tracts',
};

/** Michael's 2026-10-18 wording for MBT; MHT follows the same form. Same text as the Word export. */
const SEGMENT_NOTE: Partial<Record<R1Segment, string>> = {
  MBT: 'A subset of the majority-minority population, focusing on census tracts with a concentration of black or African American populations.',
  MHT: 'A subset of the majority-minority population, focusing on census tracts with a concentration of Hispanic or Latino populations.',
};

/**
 * Field names are segment-NEUTRAL (In / Out), matching the API. They used to be
 * Mmct/Nmct, which would now mean a field called `bankAppMmct` holding an MBT
 * count on an MBT row.
 */
interface R1Row {
  segment?: R1Segment;
  assessmentAreaId: string;
  assessmentAreaName: string;
  dataYear: number;
  bankAppTotal: number;
  bankAppIn: number;
  bankAppOut: number;
  bankAppInRate: number;
  vbaaAppTotal: number;
  /** null = the peer cache holds no tally for this segment. NOT zero. */
  vbaaAppIn: number | null;
  vbaaAppOut: number | null;
  vbaaAppInRate: number | null;
  appDisparity: number | null;
  appSignificance: string | null;
  bankOrigTotal: number;
  bankOrigIn: number;
  bankOrigOut: number;
  bankOrigInRate: number;
  vbaaOrigTotal: number;
  vbaaOrigIn: number | null;
  vbaaOrigOut: number | null;
  vbaaOrigInRate: number | null;
  origDisparity: number | null;
  origSignificance: string | null;
  vbaaPeerCount: number;
  /** Peer cache has no tally for this segment — render "—", never 0. */
  peerNotTallied?: boolean;
  dataSource: string;
  dataSourceYear: number | null;
}

interface R1Response {
  rows: R1Row[];
  /** All segments, ordered MMCT → MBT → MHT. Absent on older API responses. */
  segmentRows?: R1Row[];
  years: number[];
  assessmentAreas: string[];
  /** Subject bank's configured name (from PeerBenchmarkConfig). Drives the subject column header. */
  institutionName?: string | null;
  /**
   * Delineated AAs deliberately absent from these tables because they contain no
   * majority-minority tracts. Named rather than silently dropped, per Michael
   * Wallace 2026-08-25 — an analyst counting their areas needs to know why the
   * total differs from what they delineated.
   */
  excludedAssessmentAreas?: string[];
}

interface R1DistributionProps {
  teamId: string;
  /** "FRB MMCT", "FDIC MMCT", "FDIC MBT", "FDIC MHT", etc. — drives band labels and the [REGULATOR] header. */
  mmctRegulator?: string;
  /** Number of trailing years to show. Defaults to the years evaluated (those with a HMDA LAR loaded). */
  yearWindow?: number;
}

export default function R1Distribution({ teamId, mmctRegulator, yearWindow }: R1DistributionProps) {
  // Resolve regulator label: explicit prop wins, otherwise look up the team's primary regulator.
  const { data: regulatorConfig } = useQuery({
    queryKey: ['regulator-config', teamId],
    queryFn: async () => (await fairLendingApi.getRegulatorConfig(teamId)).data,
    enabled: !mmctRegulator && !!teamId,
  });
  const regulatorLabel = mmctRegulator
    || regulatorConfig?.fldConfig?.mmctLabel
    || 'MMCT';
  const isFrb = regulatorLabel.includes('FRB');
  const lowerLabel = isFrb ? FRB_LOWER_LABEL : FDIC_LOWER_LABEL;
  const upperLabel = isFrb ? FRB_UPPER_LABEL : FDIC_UPPER_LABEL;

  const currentYear = new Date().getFullYear();
  // Default: the years actually evaluated — those the bank has a HMDA LAR loaded for (Michael
  // Wallace, 2026-10-18: "There should be no R1 tables for years not analyzed"). The fixed windows stay
  // available for looking further back.
  const [windowSize, setWindowSize] = useState<number | 'evaluated'>(yearWindow ?? 'evaluated');
  const requestedYears: number[] | 'evaluated' = windowSize === 'evaluated'
    ? 'evaluated'
    : Array.from({ length: windowSize }, (_, i) => currentYear - i);

  const { data, isLoading, error } = useQuery<R1Response>({
    queryKey: ['r1-screen', teamId, requestedYears === 'evaluated' ? 'evaluated' : requestedYears.join(',')],
    queryFn: async () => {
      const res = await redliningApi.getR1Screen(teamId, requestedYears);
      return res.data;
    },
    enabled: !!teamId,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-panel-2 rounded w-1/3" />
        <div className="h-64 bg-panel-2 rounded" />
        <div className="h-64 bg-panel-2 rounded" />
      </div>
    );
  }

  if (error) {
    const msg = (error as any)?.response?.data?.error || (error as Error).message || 'Failed to load R1 data';
    if (msg.includes('LEI')) {
      return <SetupGate message="Set your institution's LEI under Peer Benchmarking before running R1." />;
    }
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">R1 error: {msg}</div>;
  }

  if (!data || data.rows.length === 0) {
    return <SetupGate message="No R1 data yet. Configure your assessment areas and ingest VBAA peer data to populate this screen." />;
  }

  // Group by segment, then by AA, years descending.
  //
  // `segmentRows` carries all three; older API responses only have `rows`, which
  // is MMCT, so fall back to treating those as the MMCT segment rather than
  // rendering nothing.
  const allRows: R1Row[] = data.segmentRows?.length ? data.segmentRows : data.rows;

  const groupByAA = (rows: R1Row[]) => {
    const byAA = new Map<string, { name: string; rowsByYear: Map<number, R1Row> }>();
    for (const r of rows) {
      if (!byAA.has(r.assessmentAreaId)) {
        byAA.set(r.assessmentAreaId, { name: r.assessmentAreaName, rowsByYear: new Map() });
      }
      byAA.get(r.assessmentAreaId)!.rowsByYear.set(r.dataYear, r);
    }
    return Array.from(byAA.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));
  };

  // Only segments that actually produced rows. Michael scoped the sub-type tables
  // to the presence of such TRACTS in an AA, and the backend already applied that
  // rule — an absent segment here means no AA qualified, so no empty table.
  const segmentGroups = SEGMENT_ORDER
    .map((segment) => ({
      segment,
      aas: groupByAA(allRows.filter((r) => (r.segment ?? 'MMCT') === segment)),
    }))
    .filter((g) => g.aas.length > 0);

  // Kept for the fallback-note scan below; the tables now render per segment.
  // The years the server resolved (evaluated mode) or the fixed window, newest left.
  const years: number[] = requestedYears === 'evaluated' ? data.years : requestedYears;
  const orderedYears = [...years].sort((a, b) => b - a);

  // Detect any prior-year fallbacks for the disclaimer footer.
  const fallbacks = data.rows
    .filter(r => r.dataSource === 'PRIOR_YEAR' && r.dataSourceYear)
    .map(r => `${r.dataYear} → ${r.dataSourceYear}`);
  const uniqueFallbacks = [...new Set(fallbacks)];

  const minYear = orderedYears[orderedYears.length - 1];
  const maxYear = orderedYears[0];

  // Subject-bank column header — dynamic per the team's configured institution
  // (replaces the former hardcoded "TBoFC" placeholder).
  const subjectLabel = abbreviateInstitution(data.institutionName);
  const subjectName = data.institutionName?.trim() || 'Subject bank';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {/* Michael Wallace, Redlining template 2026-10-18: no "Critical Risk Factor" kicker; this heading. */}
          <h2 className="text-lg font-semibold text-ink">R1: MMCT Application and Origination Disparities in MMCTs</h2>
          <p className="text-sm text-ink-3">
            HMDA {minYear === maxYear ? maxYear : `${minYear} through ${maxYear}`} · Per Assessment Area × Minority Concentration · {regulatorLabel}
          </p>
          {/*
            Areas with no MMCTs stay OUT of the tables (Michael Wallace,
            2026-08-25) but are named here, so an analyst comparing this list
            against what they delineated can see why the counts differ rather
            than wondering whether something failed.
          */}
          {data.excludedAssessmentAreas && data.excludedAssessmentAreas.length > 0 && (
            <p className="text-sm text-ink-3 mt-1">
              <span className="font-medium text-ink-2">AAs with no MMCTs:</span>{' '}
              {data.excludedAssessmentAreas.join(' and ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm shrink-0">
          <label className="text-ink-3">Years:</label>
          <select
            value={windowSize}
            onChange={(e) => setWindowSize(e.target.value === 'evaluated' ? 'evaluated' : Number(e.target.value))}
            className="rounded border border-rule px-2 py-1 text-sm"
          >
            <option value="evaluated">Years evaluated</option>
            <option value={3}>Last 3</option>
            <option value={5}>Last 5</option>
            <option value={10}>Last 10</option>
          </select>
        </div>
      </div>

      {/*
        Three sets of tables, in Michael Wallace's order (2026-10-05): "MMCT
        tables for each AA, separate MBT tables for each AA, and separate MHT
        tables for each AA, in that order." A segment only appears when some AA
        contains tracts of that kind — the backend applies that rule, so an
        absent segment means none qualified rather than an empty table.
      */}
      {segmentGroups.map(({ segment, aas }) => (
        <section key={segment} className="space-y-6">
          <div className="border-l-4 border-ink-3/40 pl-3">
            <h3 className="text-base font-semibold text-ink">
              {segment} = {SEGMENT_TITLE[segment]}
            </h3>
            {SEGMENT_NOTE[segment] && (
              <p className="text-xs text-ink-3 mt-0.5">{SEGMENT_NOTE[segment]}</p>
            )}
          </div>

          <R1Table
            title="HMDA Applications"
            subtitle="Action Taken = 1, 2, 3, 4, 5, 7, 8 [excludes 6]"
            years={orderedYears}
            aas={aas}
            kind="apps"
            lowerLabel={lowerLabel}
            upperLabel={upperLabel}
            subjectLabel={subjectLabel}
            subjectName={subjectName}
          />

          <R1Table
            title="HMDA Originations"
            subtitle="Action Taken = 1"
            years={orderedYears}
            aas={aas}
            kind="orig"
            lowerLabel={lowerLabel}
            upperLabel={upperLabel}
            subjectLabel={subjectLabel}
            subjectName={subjectName}
          />
        </section>
      ))}

      {/* Legend per Wallace xlsx */}
      <div className="text-xs text-ink-3 leading-relaxed bg-panel-2 border border-rule rounded p-3">
        <p>
          <span className="font-medium text-ink-2">Key:</span> Any disparity in application or origination
          rates to MMCTs is shown in <span className="text-red-700 font-semibold">red font</span> within
          the &quot;Diff.&quot; cells. The &quot;Sig.&quot; cells indicate which disparities are statistically
          significant at an elevated level of fair lending risk (<code>&lt;1%</code> or <code>&lt;5%</code>).
          Where there is no disparity in MMCTs, the &quot;Diff.&quot; cells will use{' '}
          <span className="text-emerald-700 font-semibold">green font</span> led by a negative sign, and the
          &quot;Sig.&quot; cell will show <code>NA</code>.
        </p>
        {uniqueFallbacks.length > 0 && (
          <p className="mt-2 italic text-amber-700">
            Note: the following years are tentatively compared to prior-year HMDA aggregate data (current-year
            aggregate not yet published): {uniqueFallbacks.join(', ')}.
          </p>
        )}
      </div>
    </div>
  );
}

interface R1TableProps {
  title: string;
  subtitle: string;
  years: number[];
  aas: Array<[string, { name: string; rowsByYear: Map<number, R1Row> }]>;
  kind: 'apps' | 'orig';
  lowerLabel: string;
  upperLabel: string;
  /** Subject-bank column header (abbreviated) + full name for the tooltip. */
  subjectLabel: string;
  subjectName: string;
}

function R1Table({ title, subtitle, years, aas, kind, lowerLabel, upperLabel, subjectLabel, subjectName }: R1TableProps) {
  return (
    <div className="bg-panel rounded-lg border border-rule overflow-hidden">
      <div className="px-4 py-3 border-b border-rule bg-panel-2">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        <p className="text-xs text-ink-3">{subtitle}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr className="bg-panel-2 text-ink-2">
              <th rowSpan={2} className="px-3 py-2 text-left font-medium border-r border-rule sticky left-0 bg-panel-2 z-10 min-w-[14rem]">Assessment Area</th>
              <th rowSpan={2} className="px-3 py-2 text-left font-medium border-r border-rule min-w-[8rem]">Minority Concentration</th>
              {years.map(y => (
                <th key={y} colSpan={6} className="px-2 py-2 text-center font-medium border-r border-rule">{y} HMDA {kind === 'apps' ? 'Apps' : 'Orig'}</th>
              ))}
            </tr>
            <tr className="bg-panel-2 text-ink-3">
              {years.map(y => (
                <YearSubHeaderCells key={y} subjectLabel={subjectLabel} subjectName={subjectName} />
              ))}
            </tr>
          </thead>
          <tbody>
            {aas.map(([aaId, aaData]) => (
              <AaRows
                key={aaId}
                aaName={aaData.name}
                rowsByYear={aaData.rowsByYear}
                years={years}
                kind={kind}
                lowerLabel={lowerLabel}
                upperLabel={upperLabel}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YearSubHeaderCells({ subjectLabel, subjectName }: { subjectLabel: string; subjectName: string }) {
  const headers = [subjectLabel, '%', 'VBAA', '%', 'Diff.', 'Sig.'];
  return (
    <>
      {headers.map((h, i) => (
        <th
          key={i}
          title={i === 0 ? subjectName : undefined}
          className={clsx(
            'px-2 py-1.5 text-right font-medium border-l border-rule',
            i === 5 && 'border-r border-rule',
          )}
        >
          {h}
        </th>
      ))}
    </>
  );
}

function AaRows({
  aaName,
  rowsByYear,
  years,
  kind,
  lowerLabel,
  upperLabel,
}: {
  aaName: string;
  rowsByYear: Map<number, R1Row>;
  years: number[];
  kind: 'apps' | 'orig';
  lowerLabel: string;
  upperLabel: string;
}) {
  return (
    <>
      {/* Control row (NMCT — lower band) */}
      <tr className="border-t border-rule hover:bg-panel-2">
        <td rowSpan={2} className="px-3 py-2 align-top font-medium text-ink border-r border-rule sticky left-0 bg-panel z-10">{aaName}</td>
        <td className="px-3 py-2 text-ink-2 border-r border-rule">{lowerLabel}</td>
        {years.map(y => {
          const r = rowsByYear.get(y);
          return r ? (
            <BandCells key={y} row={r} kind={kind} band="control" />
          ) : (
            <EmptyCells key={y} />
          );
        })}
      </tr>

      {/* PBG row (MMCT — upper band) */}
      <tr className="border-b border-rule hover:bg-panel-2">
        <td className="px-3 py-2 text-ink-2 border-r border-rule">{upperLabel}</td>
        {years.map(y => {
          const r = rowsByYear.get(y);
          return r ? (
            <BandCells key={y} row={r} kind={kind} band="pbg" />
          ) : (
            <EmptyCells key={y} />
          );
        })}
      </tr>
    </>
  );
}

function BandCells({ row, kind, band }: { row: R1Row; kind: 'apps' | 'orig'; band: 'control' | 'pbg' }) {
  const bankTotal = kind === 'apps' ? row.bankAppTotal : row.bankOrigTotal;
  const vbaaTotal = kind === 'apps' ? row.vbaaAppTotal : row.vbaaOrigTotal;

  // Subject-bank and VBAA volumes for this band. "pbg" is the in-segment band
  // (MMCT, MBT or MHT depending on the table); "control" is everything else.
  const bankBand = band === 'pbg'
    ? (kind === 'apps' ? row.bankAppIn : row.bankOrigIn)
    : (kind === 'apps' ? row.bankAppOut : row.bankOrigOut);

  // Peer figures are nullable: null means the peer cache carries no tally for
  // this segment, which is NOT the same as the peers having done no lending
  // there. Rendering it as 0 would turn a gap in our cache into a finding about
  // the market, so it renders as an em dash instead.
  const vbaaBand = band === 'pbg'
    ? (kind === 'apps' ? row.vbaaAppIn : row.vbaaOrigIn)
    : (kind === 'apps' ? row.vbaaAppOut : row.vbaaOrigOut);

  const bankPct = bankTotal > 0 ? (bankBand / bankTotal) * 100 : null;
  const vbaaPct = vbaaBand !== null && vbaaTotal > 0 ? (vbaaBand / vbaaTotal) * 100 : null;

  const disparityRaw = band === 'pbg' ? (kind === 'apps' ? row.appDisparity : row.origDisparity) : null;
  const sig = band === 'pbg' ? (kind === 'apps' ? row.appSignificance : row.origSignificance) : null;

  // Restore Wallace's signed convention: when sig === 'NA' the bank > peer, so display negative.
  const displayDiff = disparityRaw === null
    ? null
    : (sig === 'NA' ? -disparityRaw : disparityRaw);

  return (
    <>
      <td className="px-2 py-1.5 text-right tabular-nums border-l border-rule">{formatInt(bankBand)}</td>
      <td className="px-2 py-1.5 text-right tabular-nums border-l border-rule text-ink-3">{formatPct(bankPct)}</td>
      <td
        className="px-2 py-1.5 text-right tabular-nums border-l border-rule"
        title={vbaaBand === null ? 'Peer cache holds no tally for this segment — re-warm VBAA peer data' : undefined}
      >
        {vbaaBand === null ? <span className="text-ink-3">—</span> : formatInt(vbaaBand)}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums border-l border-rule text-ink-3">{formatPct(vbaaPct)}</td>
      <td className={clsx(
        'px-2 py-1.5 text-right tabular-nums border-l border-rule',
        band === 'pbg' && displayDiff !== null && displayDiff > 0 && 'text-red-700 font-semibold',
        band === 'pbg' && displayDiff !== null && displayDiff <= 0 && 'text-emerald-700 font-semibold',
      )}>
        {band === 'pbg' ? formatDiff(displayDiff) : '—'}
      </td>
      <td className={clsx(
        'px-2 py-1.5 text-right tabular-nums border-l border-r border-rule',
        band === 'pbg' && (sig === '<1%' || sig === '<5%') && 'text-red-700 font-semibold',
        band === 'pbg' && sig === '<10%' && 'text-orange-700 font-semibold',
      )}>
        {band === 'pbg' ? (sig ?? '—') : '—'}
      </td>
    </>
  );
}

function EmptyCells() {
  return (
    <>
      {[0, 1, 2, 3, 4, 5].map(i => (
        <td
          key={i}
          className={clsx(
            'px-2 py-1.5 text-right text-ink-4 border-l border-rule',
            i === 5 && 'border-r border-rule',
          )}
        >
          —
        </td>
      ))}
    </>
  );
}

function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function formatPct(pct: number | null): string {
  if (pct === null || isNaN(pct)) return '—';
  return `${pct.toFixed(2)}%`;
}

function formatDiff(diff: number | null): string {
  if (diff === null || isNaN(diff)) return '—';
  // Wallace shows positive (red) and negative (green with `-`) values to 2 decimals.
  const sign = diff < 0 ? '-' : '';
  return `${sign}${Math.abs(diff).toFixed(2)}`;
}

function SetupGate({ message }: { message: string }) {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800">
      {message}
    </div>
  );
}
